import { isTerminal, type RunEventPayload, type RunStatus } from './contract';

/**
 * Run state assembled from broadcasts (P0-WEB-05).
 *
 * Two rules from `docs/api/realtime-channels.json`, and both exist because
 * broadcasts are queued (`BroadcastDomainEvent` is `ShouldQueue`) and are
 * therefore only as ordered as the queue:
 *
 *  1. **Monotonic by `occurred_at`.** "Without this a 40% update landing after
 *     60% makes the bar go backwards."
 *  2. **Terminal is final.** A run that has succeeded, failed or been cancelled
 *     will not change again, so a late progress event with a newer timestamp
 *     must not resurrect it as running.
 *
 * Realtime is an accelerator, never the source of truth — so this store is
 * seeded from, and re-seeded by, whatever the HTTP loaders return. `reset` is
 * how a reconnect hands authority back to the server.
 */

export interface RunState {
  readonly runId: string;
  readonly status: RunStatus;
  /** 0–100. Held across events, so a `run_succeeded` does not blank the bar. */
  readonly progressPct: number;
  readonly progressMessage: string | null;
  readonly progressStage: string | null;
  readonly creditsEstimated: number | null;
  readonly creditsCharged: number | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  /** The timestamp of the newest event applied — the monotonic watermark. */
  readonly occurredAt: string;
}

export type RunStoreListener = (runs: ReadonlyMap<string, RunState>) => void;

export interface RunStore {
  readonly apply: (payload: RunEventPayload) => boolean;
  readonly get: (runId: string) => RunState | undefined;
  readonly subscribe: (listener: RunStoreListener) => () => void;
  readonly getSnapshot: () => ReadonlyMap<string, RunState>;
  /** Drops everything. Called when the organisation changes, or on sign-out. */
  readonly reset: () => void;
}

const EMPTY: ReadonlyMap<string, RunState> = new Map();

export function createRunStore(): RunStore {
  let runs: ReadonlyMap<string, RunState> = EMPTY;
  const listeners = new Set<RunStoreListener>();

  function publish(next: ReadonlyMap<string, RunState>): void {
    runs = next;
    for (const listener of listeners) listener(runs);
  }

  return {
    /** Returns whether the event changed anything — tests and diagnostics read
     *  it; the UI does not need to. */
    apply(payload) {
      const at = Date.parse(payload.occurred_at);

      // An unorderable event cannot be shown to be safe, and the whole rule
      // exists to stop a stale one moving the bar backwards. The broadcaster
      // always sets `occurred_at` (BroadcastDomainEvent), so this is a contract
      // violation rather than a case to accommodate — but it is dropped rather
      // than thrown, because one malformed broadcast must not take down a
      // socket carrying every other run.
      if (Number.isNaN(at)) return false;

      const existing = runs.get(payload.run_id);

      if (existing !== undefined) {
        // Terminal wins over the clock. Two events for one run can arrive out
        // of order, and skew between the emitting processes means a late
        // progress event can genuinely carry the newer timestamp.
        if (isTerminal(existing.status)) return false;

        // `<=` rather than `<`: two events with the same timestamp are
        // unordered with respect to each other, so the first applied wins and
        // the result does not depend on delivery order.
        if (at <= Date.parse(existing.occurredAt)) return false;
      }

      const next = new Map(runs);
      next.set(payload.run_id, merge(existing, payload));
      publish(next);
      return true;
    },

    get(runId) {
      return runs.get(runId);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      return runs;
    },

    reset() {
      if (runs.size === 0) return;
      publish(EMPTY);
    },
  };
}

function merge(existing: RunState | undefined, payload: RunEventPayload): RunState {
  return {
    runId: payload.run_id,
    status: payload.status,
    progressPct: progressFrom(existing, payload),
    // Carried forward when the event does not carry one: `run_succeeded` has no
    // progress fields, and blanking the label at the moment a run finishes
    // reads as something having gone wrong.
    progressMessage: payload.progress_message ?? existing?.progressMessage ?? null,
    progressStage: payload.progress_stage ?? existing?.progressStage ?? null,
    creditsEstimated: payload.credits_estimated ?? existing?.creditsEstimated ?? null,
    creditsCharged: payload.credits_charged ?? existing?.creditsCharged ?? null,
    errorCode: payload.error_code ?? existing?.errorCode ?? null,
    errorMessage: payload.error_message ?? existing?.errorMessage ?? null,
    occurredAt: payload.occurred_at,
  };
}

function progressFrom(existing: RunState | undefined, payload: RunEventPayload): number {
  // A finished run reads 100% whatever the last progress event said. A bar
  // stopping at 97% next to the word "Completed" is the kind of detail that
  // makes people distrust the number.
  if (isTerminal(payload.status))
    return payload.status === 'succeeded' ? 100 : (existing?.progressPct ?? 0);

  if (typeof payload.progress_pct === 'number' && Number.isFinite(payload.progress_pct)) {
    // Clamped rather than trusted: a stage that reports 103% of its own work is
    // a bug in a worker, not a reason to overflow a progress bar.
    return Math.min(100, Math.max(0, Math.round(payload.progress_pct)));
  }

  return existing?.progressPct ?? 0;
}
